export class StorageKeys {
  static readonly AUTH_SOURCE = "_authsource";
  static readonly ACCESS_TOKEN = "_auth";
  static readonly REFRESH_TOKEN = "_refresh";
  static readonly PARENT_IDENTITY = "_parentIdentity";
  static readonly EMULATION_START = "_emulationStart";
  static readonly ATTRIBUTES = "attributes";
}

export class AuthSources {
  static readonly AWS_COGNITO = "aws/cognito";
  static readonly CAPITALRX_STS = "capitalrx/sts";
}

/**
 * These paths should not force the user back to the login page
 * when the user is signed out and tries to directly navigate to them
 */
export const nonRedirectableAuthnzPaths: string[] = [
  "/confirm",
  "/login",
  "/unsubscribe",
  "/forgot-password",
  "/update-password",
  "/sso/callback",
] as const;

export const authedOrUnauthedAllowedAuthnzPaths: string[] = ["/confirm"] as const;

export const defaultMapCoordinates = {
  lat: 40.7045389,
  lng: -74.0133233,
};

export const labelMapper = {
  deductible: "Deductible",
  out_of_pocket: "Out-of-Pocket",
  plan_pay: "Plan Maximum",
};

export const currentLocation = "Current Location";

export const PRESCRIPTION_PROPERTIES = {
  type: "Type",
  dosage: "Dosage",
  form: "Form",
  quantity: "Quantity",
  days_supply: "Days Supply",
  location: "Location",
};

export const relationshipCodeMapping = {
  "1": "Primary Cardholder",
  "2": "Spouse",
  "3": "Child",
  "4": "Other",
};

export const basisOfReimbursement: { [key: string]: string } = {
  "00": "not_specified",
  "01": "submitted",
  "03": "awp",
  "04": "usual_and_customary",
  "07": "mac_list",
  "08": "specialty_list",
  "13": "wac",
  "20": "nadac",
};

export const usualAndCustomaryMax = 499999;

export const deliveryChannels = [
  "M", // "mail",
  "S", // "specialty",
  "I", // "in_house"
];

export const rejectCodeInfo = {
  "34": {
    topic: "Requires Submission Clarification Code",
    description: "Pharmacist must submit Submission Clarification Code for this drug.",
  },
  "40": {
    topic: "Pharmacy Not Contracted With Plan On Date Of Service",
    description: "Associated pharmacy may not be contracted with your insurance yet.",
  },
  "60": {
    topic: "Age Restriction (patient is too young)",
    description: "Selected drug has age restriction limits.",
  },
  "61": {
    topic: "Drug Not Covered for Patient Gender",
    description: "Selected drug has gender restrictions.",
  },
  "66": {
    topic: "Age restriction (patient exceeds maximum age)",
    description: "Selected drug has age restriction limits.",
  },
  "70": {
    topic: "Product/Service Not Covered - Plan/Benefit Exclusion",
    description: "Product not covered. You may still purchase it, but you will have to pay the entire cost yourself.",
  },
  "79": {
    topic: "Refill Too Soon.",
    description: "Refill too soon. This is to ensure members are taking medications as directed.",
  },
  "7W": {
    topic: "Refills Exceed Maximum Allowed",
    description: "Refill limit exceeded.",
  },
  "7X": {
    topic: "Days Supply Exceeds Plan Limitation.",
    description: "Days supply of selected drug exceeds plan limitation.",
  },
  "88": {
    topic: "Drug Utilization Review Required",
    description:
      "Drug utilization review programs help to ensure that prescriptions for outpatient drugs are appropriate, medically necessary, and not likely to result in adverse medical consequences.",
  },
  "9G": {
    topic: "Quantity Dispensed Exceeds Maximum Allowed.",
    description:
      "Quantity dispensed exceeds maximum allowed. This is to limit the risk of misuse and ensure you are taking the medication as directed.",
  },
  "75": {
    topic: "Prior Authorization",
    description: "As a reminder, this prescription requires a Prior Authorization from your physician.",
  },
  "76": {
    topic: "Plan Limitations Exceeded",
    description:
      "Plan Limitations Exceeded. You may continue to view prices. To inquire further about reason for reject, please contact your plan sponsor or local pharmacist.",
  },
  "608": {
    topic: "Step Therapy Required",
    description:
      "Step therapy means trying less expensive options before trying drugs that cost more. One way we do that is through our step therapy program. It works to make sure you get the safest, most effective and reasonably-priced drug available.",
  },
};

// Some rejects that can be suppressed without issues
export const suppressableRejectCodes = [
  "34", // M/I Submission Clarification Code
  "60", // Product/Service Not Covered For Patient Age
  "61", // Product/Service Not Covered For Patient Gender
  "66", // Patient Age Exceeds Maximum Age.
  "70", // Product/Service Not Covered - Plan/Benefit
  "75", // prior authorization required
  "76", // Plan limitations exceeeded
  "78", // Cost Exceeds Maximum
  "79", // Refill Too Soon
  "7W", // Refills Exceed allowable Refills
  "88", // DUR Reject Error
  "9G", // Quantity dispensed exceeds maximum
  "GE", // M/I Percentage Tax Amount Submitted.
  "HA", // M/I Regulatory Fee Amount Submitted.
  "HE", // M/I Percentage Tax Rate Submitted.
  "608", // Step Therapy, Alternate Drug Therapy Required Prior To Use
];

// 1000 (milliseconds) * 60 (seconds) * 15 (minutes)]
export const userTimeout = 1000 * 60 * 15;

// Show idle timer modal when 60 seconds remain before timeout
export const showIdleTimerWithSecondsLeft = 60;

export const monthOptions = [
  {
    label: "January",
    value: 1,
  },
  {
    label: "February",
    value: 2,
  },
  {
    label: "March",
    value: 3,
  },
  {
    label: "April",
    value: 4,
  },
  {
    label: "May",
    value: 5,
  },
  {
    label: "June",
    value: 6,
  },
  {
    label: "July",
    value: 7,
  },
  {
    label: "August",
    value: 8,
  },
  {
    label: "September",
    value: 9,
  },
  {
    label: "October",
    value: 10,
  },
  {
    label: "November",
    value: 11,
  },
  {
    label: "December",
    value: 12,
  },
];

export const rejectReason = {
  notCovered: "70",
  priorAuthRequired: "75",
  productServiceNotCovered: "70",
};

export const client_ids = {
  CDCOM: "CDPHP", // CDPHP commercial
  CDCAID: "CDPHP", // CDPHP Medicaid
  CDCARE: "CDPHP", // CDPHP Medicare
};

// see also MEDICARE_PART_B_STRATEGIES on the backend
export const medicarePartBStrategies = [
  "medicare_actuarially_equivalent_mapd",
  "medicare_basic_alternative_mapd",
  "medicare_defined_standard_mapd",
  "medicare_employer_group_waiver_plan_mapd",
  "medicare_enhanced_alternative_mapd",
  "medicare_part_b_only_ma",
];

// see also MEDICARE_PART_D_STRATEGIES on the backend
export const medicarePartDStrategies = [
  "medicare_actuarially_equivalent_mapd",
  "medicare_basic_alternative_mapd",
  "medicare_defined_standard_mapd",
  "medicare_employer_group_waiver_plan_mapd",
  "medicare_enhanced_alternative_mapd",
  "medicare_actuarially_equivalent_pdp",
  "medicare_basic_alternative_pdp",
  "medicare_defined_standard_pdp",
  "medicare_employer_group_waiver_plan_pdp",
  "medicare_enhanced_alternative_pdp",
  "medicare_prescription_payment_plan",
];

/* if we get rejects that come in matching a non suppressable reject,
   dont bother letting the user attempt to override and reprocess */
export const nonSuppressableRejectCodes = ["MR", "7X"];

export class SearchParamKeys {
  static readonly MEMBER_ELIGIBILITY_MEMBER_ID = "member";
  static readonly EXPERIENCE_AS_OF_DATE = "as_of_date";
  static readonly EXPERIENCE_QUERY_IDENTIFIER = "client";
  static readonly PERSON_CODE = "person_code";
}

export const STS_TOKEN_REFRESH_SECONDS = 30;

export const states = [
  {
    label: "Alabama",
    value: "AL",
  },
  {
    label: "Alaska",
    value: "AK",
  },
  {
    label: "American Samoa",
    value: "AS",
  },
  {
    label: "Arizona",
    value: "AZ",
  },
  {
    label: "Arkansas",
    value: "AR",
  },
  {
    label: "California",
    value: "CA",
  },
  {
    label: "Colorado",
    value: "CO",
  },
  {
    label: "Connecticut",
    value: "CT",
  },
  {
    label: "Delaware",
    value: "DE",
  },
  {
    label: "District Of Columbia",
    value: "DC",
  },
  {
    label: "Federated States Of Micronesia",
    value: "FM",
  },
  {
    label: "Florida",
    value: "FL",
  },
  {
    label: "Georgia",
    value: "GA",
  },
  {
    label: "Guam",
    value: "GU",
  },
  {
    label: "Hawaii",
    value: "HI",
  },
  {
    label: "Idaho",
    value: "ID",
  },
  {
    label: "Illinois",
    value: "IL",
  },
  {
    label: "Indiana",
    value: "IN",
  },
  {
    label: "Iowa",
    value: "IA",
  },
  {
    label: "Kansas",
    value: "KS",
  },
  {
    label: "Kentucky",
    value: "KY",
  },
  {
    label: "Louisiana",
    value: "LA",
  },
  {
    label: "Maine",
    value: "ME",
  },
  {
    label: "Marshall Islands",
    value: "MH",
  },
  {
    label: "Maryland",
    value: "MD",
  },
  {
    label: "Massachusetts",
    value: "MA",
  },
  {
    label: "Michigan",
    value: "MI",
  },
  {
    label: "Minnesota",
    value: "MN",
  },
  {
    label: "Mississippi",
    value: "MS",
  },
  {
    label: "Missouri",
    value: "MO",
  },
  {
    label: "Montana",
    value: "MT",
  },
  {
    label: "Nebraska",
    value: "NE",
  },
  {
    label: "Nevada",
    value: "NV",
  },
  {
    label: "New Hampshire",
    value: "NH",
  },
  {
    label: "New Jersey",
    value: "NJ",
  },
  {
    label: "New Mexico",
    value: "NM",
  },
  {
    label: "New York",
    value: "NY",
  },
  {
    label: "North Carolina",
    value: "NC",
  },
  {
    label: "North Dakota",
    value: "ND",
  },
  {
    label: "Northern Mariana Islands",
    value: "MP",
  },
  {
    label: "Ohio",
    value: "OH",
  },
  {
    label: "Oklahoma",
    value: "OK",
  },
  {
    label: "Oregon",
    value: "OR",
  },
  {
    label: "Palau",
    value: "PW",
  },
  {
    label: "Pennsylvania",
    value: "PA",
  },
  {
    label: "Puerto Rico",
    value: "PR",
  },
  {
    label: "Rhode Island",
    value: "RI",
  },
  {
    label: "South Carolina",
    value: "SC",
  },
  {
    label: "South Dakota",
    value: "SD",
  },
  {
    label: "Tennessee",
    value: "TN",
  },
  {
    label: "Texas",
    value: "TX",
  },
  {
    label: "Utah",
    value: "UT",
  },
  {
    label: "Vermont",
    value: "VT",
  },
  {
    label: "Virgin Islands",
    value: "VI",
  },
  {
    label: "Virginia",
    value: "VA",
  },
  {
    label: "Washington",
    value: "WA",
  },
  {
    label: "West Virginia",
    value: "WV",
  },
  {
    label: "Wisconsin",
    value: "WI",
  },
  {
    label: "Wyoming",
    value: "WY",
  },
];
export const M3P_ELECTION_DECISION_APPROVE_NEW = "1";
export const M3P_ELECTION_DECISION_APPROVE_REINSTATE = "2";
export const M3P_ELECTION_DECISION_APPROVE_RETROACTIVE = "3";
export const M3P_ELECTION_DECISION_APPROVE_SUBSEQUENT_PARTICIPATION = "8";
export const M3P_ELECTION_DECISION_APPROVE_AUTO_RENEWAL = "11";

export const M3P_ELECTION_DECISIONS_APPROVE = {
  [M3P_ELECTION_DECISION_APPROVE_NEW]: "Approved - New Election",
  [M3P_ELECTION_DECISION_APPROVE_REINSTATE]: "Approved - Reinstatement",
  [M3P_ELECTION_DECISION_APPROVE_RETROACTIVE]: "Approved - Retroactive",
  [M3P_ELECTION_DECISION_APPROVE_SUBSEQUENT_PARTICIPATION]: "Approved - Subsequent Participation",
  [M3P_ELECTION_DECISION_APPROVE_AUTO_RENEWAL]: "Approved - Auto Renewal",
};

export const M3P_TERMINATION_AUTORENEWAL = "4";

export const PENDED_STATUS = "pended";
export const IN_PROGRESS_OUTCOME = "in_progress";

export const VISION = "vision";
export const DENTAL = "dental";
export const MEDICAL = "medical";
export const MEDICAL_CLAIM = "medical_claim";
export const CLAIM = "claim";
export const PHARMACY = "pharmacy";
export const PRIOR_AUTHORIZATION = "prior_authorization";
export const BASE_CLAIM_TYPES = [VISION, DENTAL] as const;
export const CLAIMS_TYPES = [...BASE_CLAIM_TYPES, MEDICAL, PHARMACY] as const;
export const RECENT_ACTIVITY_FILTER_TYPES = [...CLAIMS_TYPES, PRIOR_AUTHORIZATION] as const;

export const RECENT_ACTIVITY_RETURN_TYPES = [...BASE_CLAIM_TYPES, CLAIM, MEDICAL_CLAIM, PRIOR_AUTHORIZATION] as const;
export type RecentActivityReturnTypes = (typeof RECENT_ACTIVITY_RETURN_TYPES)[number];
export type ClaimsTypes = (typeof CLAIMS_TYPES)[number];
export type RecentActivityFilters = (typeof RECENT_ACTIVITY_FILTER_TYPES)[number];
export const TOOTH_CODE_MAPPING = {
  "A": "Upper Right Bicuspid (2nd)",
  "B": "Upper Right Bicuspid (1st)",
  "C": "Upper Right Canine (Eye tooth / Cuspid)",
  "D": "Upper Right Incisor (Lateral)",
  "E": "Upper Right Incisor (Central)",
  "F": "Upper Left Incisor (Central)",
  "G": "Upper Left Incisor (Lateral)",
  "H": "Upper Left Canine (Eye tooth / Cuspid)",
  "I": "Upper Left Bicuspid (1st)",
  "J": "Upper Left Bicuspid (2nd)",
  "K": "Lower Left Bicuspid (2nd)",
  "L": "Lower Left Bicuspid (1st)",
  "M": "Lower Left Canine (Eye tooth / Cuspid)",
  "N": "Lower Left Incisor (Lateral)",
  "O": "Lower Left Incisor (Central)",
  "P": "Lower Right Incisor (Central)",
  "Q": "Lower Right Incisor (Lateral)",
  "R": "Lower Right Canine (Eye tooth / Cuspid)",
  "S": "Lower Right Bicuspid (1st)",
  "T": "Lower Right Bicuspid (2nd)",
  "1": "Upper Right Wisdom Tooth (3rd Molar)",
  "2": "Upper Right Molar (2nd Molar)",
  "3": "Upper Right Molar (1st Molar)",
  "4": "Upper Right Bicuspid (2nd)",
  "5": "Upper Right Bicuspid (1st)",
  "6": "Upper Right Canine (Eye tooth / Cuspid)",
  "7": "Upper Right Incisor (Lateral)",
  "8": "Upper Right Incisor (Central)",
  "9": "Upper Left Incisor (Central)",
  "10": "Upper Left Incisor (Lateral)",
  "11": "Upper Left Canine (Eye tooth / Cuspid)",
  "12": "Upper Left Bicuspid (1st)",
  "13": "Upper Left Bicuspid (2nd)",
  "14": "Upper Left Molar (1st Molar)",
  "15": "Upper Left Molar (2nd Molar)",
  "16": "Upper Left Wisdom Tooth (3rd Molar)",
  "17": "Lower Left Wisdom Tooth (3rd Molar)",
  "18": "Lower Left Molar (2nd Molar)",
  "19": "Lower Left Molar (1st Molar)",
  "20": "Lower Left Bicuspid (2nd)",
  "21": "Lower Left Bicuspid (1st)",
  "22": "Lower Left Canine (Eye tooth / Cuspid)",
  "23": "Lower Left Incisor (Lateral)",
  "24": "Lower Left Incisor (Central)",
  "25": "Lower Right Incisor (Central)",
  "26": "Lower Right Incisor (Lateral)",
  "27": "Lower Right Canine (Eye tooth / Cuspid)",
  "28": "Lower Right Bicuspid (1st)",
  "29": "Lower Right Bicuspid (2nd)",
  "30": "Lower Right Molar (1st Molar)",
  "31": "Lower Right Molar (2nd Molar)",
  "32": "Lower Right Wisdom Tooth (3rd Molar)",
};

export const PHARMACY_FILTERING_NPI_OPTION = "npi";

export const PHARMACY_FILTERING_OPTIONS = [
    PHARMACY_FILTERING_NPI_OPTION,
] as const;
